import { useState, useEffect, useRef, useCallback } from 'react'
import { writingAssistantService } from '../../services/writingAssistantService'
import type { WritingAssistantListItem } from '../../types/writing'
import { markdownToHtml, htmlToMarkdown } from '../../utils/contentConverters'

interface UseWritingAssistantContentProps {
  writingAssistants: WritingAssistantListItem[]
  selectedLanguages: Record<string, string>
  isClosedCrisis: boolean
}

export function useWritingAssistantContent({
  writingAssistants,
  selectedLanguages,
  isClosedCrisis,
}: UseWritingAssistantContentProps) {
  const [contentStates, setContentStates] = useState<Record<string, string>>({})
  const [originalContentStates, setOriginalContentStates] = useState<Record<string, string>>({})
  const [lastSavedAt, setLastSavedAt] = useState<Record<string, Date | null>>({})
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({})
  const debouncedSaveFunctions = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const prevContentStates = useRef<Record<string, string>>({})
  const prevOriginalContentStates = useRef<Record<string, string>>({})

  // Get current content for a writing assistant based on selected language
  const getCurrentContent = useCallback(
    (writingAssistant: WritingAssistantListItem) => {
      const selectedLanguage = selectedLanguages[writingAssistant.id] || 'nl'

      if (selectedLanguage === 'nl') {
        // For Dutch, always use the original generated content
        return writingAssistant.generatedContent
          ? markdownToHtml(writingAssistant.generatedContent)
          : ''
      } else {
        // For other languages, use the translated content if available
        const translations = writingAssistant.translations || {}
        const translatedContent = translations[selectedLanguage]

        if (translatedContent) {
          // Handle both old format (string) and new format (object with content and version)
          if (typeof translatedContent === 'string') {
            return markdownToHtml(translatedContent)
          } else if (translatedContent.content) {
            return markdownToHtml(translatedContent.content)
          }
        }

        return ''
      }
    },
    [selectedLanguages]
  )

  // Initialize content states when writing assistants or selected languages change
  useEffect(() => {
    const newContentStates: Record<string, string> = {}
    const newOriginalContentStates: Record<string, string> = {}

    writingAssistants.forEach(wa => {
      const selectedLanguage = selectedLanguages[wa.id] || 'nl'
      const currentContent = getCurrentContent(wa)
      const contentKey = `${wa.id}-${selectedLanguage}`

      // Update content state: if it doesn't exist or matches the old original (no user changes), update with new content
      const existingContent = prevContentStates.current[contentKey]
      const oldOriginal = prevOriginalContentStates.current[contentKey]

      if (!existingContent || (oldOriginal && existingContent === oldOriginal)) {
        newContentStates[contentKey] = currentContent
      } else {
        // User has made changes, keep them
        newContentStates[contentKey] = existingContent
      }

      // Always update original content state for this language
      newOriginalContentStates[contentKey] = currentContent
    })

    setContentStates(newContentStates)
    setOriginalContentStates(newOriginalContentStates)

    // Update refs for next comparison
    prevContentStates.current = newContentStates
    prevOriginalContentStates.current = newOriginalContentStates
  }, [writingAssistants, selectedLanguages, getCurrentContent])

  // Save content to API
  const saveContent = useCallback(
    async (writingAssistantId: string, htmlContent: string, selectedLanguage: string) => {
      if (isClosedCrisis) return

      // Convert HTML back to markdown for API storage
      const markdownContent = htmlToMarkdown(htmlContent)
      const contentKey = `${writingAssistantId}-${selectedLanguage}`

      // Skip saving if content is unchanged from original
      const originalContent = originalContentStates[contentKey]
      if (originalContent === undefined || htmlToMarkdown(originalContent) === markdownContent)
        return

      setIsSaving(prev => ({ ...prev, [writingAssistantId]: true }))
      try {
        await writingAssistantService.updateWritingAssistantContent(
          writingAssistantId,
          markdownContent
        )
        setLastSavedAt(prev => ({ ...prev, [writingAssistantId]: new Date() }))
        // Update original content state after successful save
        setOriginalContentStates(prev => ({ ...prev, [contentKey]: htmlContent }))
      } catch (error) {
        console.error('Error saving content:', error)
      } finally {
        setIsSaving(prev => ({ ...prev, [writingAssistantId]: false }))
      }
    },
    [isClosedCrisis, originalContentStates]
  )

  // Handle content change with debouncing
  const handleContentChange = useCallback(
    (writingAssistantId: string, content: string) => {
      if (isClosedCrisis) return

      const selectedLanguage = selectedLanguages[writingAssistantId] || 'nl'
      const contentKey = `${writingAssistantId}-${selectedLanguage}`

      setContentStates(prev => ({
        ...prev,
        [contentKey]: content,
      }))

      // Clear existing timeout for this writing assistant
      if (debouncedSaveFunctions.current[writingAssistantId]) {
        clearTimeout(debouncedSaveFunctions.current[writingAssistantId])
      }

      // Set new timeout for auto-save
      debouncedSaveFunctions.current[writingAssistantId] = setTimeout(() => {
        saveContent(writingAssistantId, content, selectedLanguage)
      }, 1500)
    },
    [isClosedCrisis, saveContent, selectedLanguages]
  )

  // Get saving status text
  const getSavingStatusText = useCallback(
    (writingAssistantId: string) => {
      if (isSaving[writingAssistantId]) {
        return 'Opslaan...'
      }

      if (lastSavedAt[writingAssistantId]) {
        const secondsAgo = Math.floor(
          (Date.now() - lastSavedAt[writingAssistantId]!.getTime()) / 1000
        )
        if (secondsAgo < 60) {
          return `Automatisch opgeslagen ${secondsAgo} seconden geleden`
        } else {
          const minutesAgo = Math.floor(secondsAgo / 60)
          return `Automatisch opgeslagen ${minutesAgo} minuten geleden`
        }
      }

      return null
    },
    [isSaving, lastSavedAt]
  )

  // Get content state for a specific writing assistant and language
  const getContentState = useCallback(
    (writingAssistantId: string) => {
      const selectedLanguage = selectedLanguages[writingAssistantId] || 'nl'
      const contentKey = `${writingAssistantId}-${selectedLanguage}`
      return contentStates[contentKey] || ''
    },
    [contentStates, selectedLanguages]
  )

  return {
    getContentState,
    isSaving,
    handleContentChange,
    saveContent,
    getSavingStatusText,
    getCurrentContent,
  }
}
